/** The change record attached to a work item: the chip that shows it, and the picker that
 *  attaches it.
 *
 * Two rules the interface makes visible rather than explaining:
 *
 * * **The status is a copy, and it says how old it is.** A change status on screen looks
 *   identical whether it was read a minute ago or last week, and a chip reading "Approved"
 *   when the change was rejected this morning is something somebody may act on. Anything
 *   the server considers stale is marked, with a way to re-read it on the spot.
 * * **Nothing here changes the change.** Attaching a record to a work item is a note in
 *   this application. Raising, editing and approving all stay in change control's own web
 *   app, under its own sign-in and its own approvers.
 */

import { useEffect, useRef, useState } from 'react'
import { Link2, RefreshCw, Unlink } from 'lucide-react'

import { Button, Can, ErrorState, Pill, inputClass } from '@/components/ui'
import type { Tone } from '@/lib/domain'
import { Permissions } from '@/lib/session'
import type { WorkItem } from '@/features/workbench/useWork'
import {
  useChangeSearch,
  useLinkChange,
  useRefreshChange,
  useUnlinkChange,
} from '@/features/workbench/useChanges'

/** Change control's vocabulary, toned for a reader who does not know it.
 *
 * Unknown statuses fall through to neutral rather than being force-fitted to a colour —
 * CAB may add states, and guessing whether a new one is good or bad would be worse than
 * saying nothing.
 */
function statusTone(status: string): Tone {
  const value = status.toLowerCase()
  if (value.includes('approved') || value.includes('closed') || value.includes('complete'))
    return 'green'
  if (value.includes('pending') || value.includes('review') || value.includes('submitted'))
    return 'amber'
  if (value.includes('reject') || value.includes('fail')) return 'red'
  if (value.includes('progress') || value.includes('implement')) return 'info'
  return 'neutral'
}

const pretty = (status: string) => status.replace(/_/g, ' ')

export function ChangeChip({ item }: { item: WorkItem }) {
  const refresh = useRefreshChange()
  const change = item.change
  if (!change) return null

  return (
    <div className="mt-2 rounded border border-border px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={change.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-xs font-medium underline"
          style={{ color: 'var(--status-info)' }}
          onClick={(event) => event.stopPropagation()}
          title={change.title ?? undefined}
        >
          {change.number}
        </a>
        <Pill tone={statusTone(change.status)}>{pretty(change.status)}</Pill>
        {change.riskLevel ? (
          <span className="micro-label text-fg-muted">{change.riskLevel} risk</span>
        ) : null}
      </div>

      {change.isStale ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-fg-muted">
            {change.lastError
              ? 'Could not re-read this change'
              : change.hoursSinceSync === null
                ? 'Never read'
                : `Read ${Math.round(change.hoursSinceSync)}h ago`}
          </span>
          <button
            type="button"
            className="text-[11px] underline text-fg-muted hover:text-fg"
            disabled={refresh.isPending}
            onClick={(event) => {
              event.stopPropagation()
              refresh.mutate(item.id)
            }}
          >
            <RefreshCw size={10} aria-hidden className="inline" />{' '}
            {refresh.isPending ? 'Checking…' : 'Check now'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function ChangePicker({ item }: { item: WorkItem }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const panel = useRef<HTMLDivElement>(null)

  // Close on a click outside, or on Escape.
  //
  // A panel that only closes via its own button is the thing everybody complains about: it
  // sits over the board while you try to read the card behind it. Escape matters as much as
  // the click — the search box takes focus on open, so the keyboard is where hands already
  // are. Bound only while open, so there is no listener on the document for the fifty cards
  // that are not showing a picker.
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  const search = useChangeSearch(query, open)
  const link = useLinkChange()
  const unlink = useUnlinkChange()

  if (item.change) {
    return (
      <Can permission={Permissions.workEdit}>
        <button
          type="button"
          className="mt-1 text-[11px] underline text-fg-muted hover:text-fg"
          disabled={unlink.isPending}
          onClick={(event) => {
            event.stopPropagation()
            unlink.mutate({ itemId: item.id, rowVersion: item.rowVersion })
          }}
        >
          <Unlink size={10} aria-hidden className="inline" /> Detach change
        </button>
      </Can>
    )
  }

  if (!open) {
    return (
      <Can permission={Permissions.workEdit}>
        <button
          type="button"
          className="mt-1 text-[11px] underline text-fg-muted hover:text-fg"
          onClick={(event) => {
            event.stopPropagation()
            setOpen(true)
          }}
        >
          <Link2 size={10} aria-hidden className="inline" /> Attach a change
        </button>
      </Can>
    )
  }

  const available = search.data?.available !== false
  const results = search.data?.changes ?? []

  return (
    <div
      ref={panel}
      className="mt-2 rounded border border-border p-2"
      onClick={(e) => e.stopPropagation()}
    >
      {!available ? (
        // Not an error. An environment with no change-control key must still work, and
        // saying so plainly beats an empty list that looks like a failed search.
        <p className="text-[11px] text-fg-muted">
          Change control is not configured for this environment.
        </p>
      ) : (
        <>
          <input
            className={`${inputClass} text-xs`}
            value={query}
            autoFocus
            placeholder="Change number or title"
            onChange={(event) => setQuery(event.target.value)}
          />
          {search.isFetching ? (
            <p className="mt-1 text-[11px] text-fg-muted">Searching change control…</p>
          ) : results.length === 0 ? (
            <p className="mt-1 text-[11px] text-fg-muted">
              {query ? 'No change matches that.' : 'No recent changes.'}
            </p>
          ) : (
            <ul className="mt-1 max-h-48 divide-y divide-border overflow-y-auto">
              {results.map((change) => (
                <li key={change.externalId}>
                  <button
                    type="button"
                    className="w-full px-1 py-1.5 text-left hover:bg-canvas"
                    disabled={link.isPending}
                    onClick={() =>
                      link.mutate(
                        {
                          itemId: item.id,
                          externalId: change.externalId,
                          rowVersion: item.rowVersion,
                        },
                        { onSuccess: () => setOpen(false) },
                      )
                    }
                  >
                    <span className="block text-xs font-medium text-fg">
                      {change.number} · {pretty(change.status)}
                    </span>
                    <span className="block truncate text-[11px] text-fg-muted">
                      {change.title}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {search.error ? <ErrorState error={search.error} /> : null}
      {link.error ? <ErrorState error={link.error} /> : null}

      <Button variant="secondary" onClick={() => setOpen(false)}>
        Close
      </Button>
    </div>
  )
}
